import { Component } from '@theme/component';
 
/**
 * Builds a nested table of contents from the h2/h3 headings inside the article
 * content, assigning each heading a stable, readable id so the anchors can jump
 * to it.
 *
 * Ids are derived from the heading text, not from its position. A heading keeps
 * an id it already carries. That way an anchor stays valid when a section is
 * added, removed or reordered, and the "jump to section" links Google shows
 * read as words rather than as "heading-6".
 *
 * @typedef {Object} Refs
 * @property {HTMLOListElement} list
 *
 * @extends {Component<Refs>}
 */
class BlogPostToc extends Component {
  requiredRefs = ['list'];
 
  connectedCallback() {
    super.connectedCallback();
    this.#build();
  }
 
  /**
   * Turns heading text into a URL-safe slug. German umlauts are transliterated
   * rather than dropped, so "Was ist Histamin?" becomes "was-ist-histamin".
   *
   * @param {string | null} text
   * @returns {string}
   */
  #slugify(text) {
    return (text ?? '')
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60)
      .replace(/-+$/g, '');
  }
 
  #build() {
    const content = document.getElementById('blog-post-contents');
    const list = this.refs.list;
    if (!content) return;
 
    const headings = content.querySelectorAll('h2, h3');
    /** @type {HTMLLIElement | null} */
    let currentMainItem = null;
    /** @type {HTMLOListElement | null} */
    let subList = null;
    /** @type {Set<string>} */
    const used = new Set();
 
    headings.forEach((heading, index) => {
      // Keep an id the heading already has, so existing links stay valid.
      let slug = heading.id || this.#slugify(heading.textContent) || `abschnitt-${index + 1}`;
 
      // Two sections can carry the same wording; make the id unique either way.
      if (used.has(slug)) {
        let n = 2;
        while (used.has(`${slug}-${n}`)) n += 1;
        slug = `${slug}-${n}`;
      }
      used.add(slug);
      heading.id = slug;
 
      if (heading.tagName === 'H2') {
        currentMainItem = document.createElement('li');
        currentMainItem.className = 'toc-main';
        currentMainItem.appendChild(this.#createLink(slug, heading.textContent));
        list.appendChild(currentMainItem);
        subList = null;
      } else if (heading.tagName === 'H3' && currentMainItem) {
        if (!subList) {
          subList = document.createElement('ol');
          subList.className = 'toc-sub';
          currentMainItem.appendChild(subList);
        }
        const subItem = document.createElement('li');
        subItem.appendChild(this.#createLink(slug, heading.textContent));
        subList.appendChild(subItem);
      }
    });
  }
 
  /**
   * @param {string} slug
   * @param {string | null} text
   */
  #createLink(slug, text) {
    const link = document.createElement('a');
    link.href = `#${slug}`;
    link.textContent = text ?? '';
    return link;
  }
}
 
if (!customElements.get('blog-post-toc')) {
  customElements.define('blog-post-toc', BlogPostToc);
}
 